import { useState } from 'react';
import { Platform, Pressable, StyleSheet, TouchableOpacity, type ViewStyle } from 'react-native';
import { useTranslation } from '../../contexts/TranslationContext';
import type { SocialMedia } from '../../lib/workflowsApi';
import { colors } from '../../theme/colors';
import { withAlpha } from '../../theme/colorUtils';
import { Box } from '../primitives/Box';
import { Sheet } from '../primitives/Sheet';
import { VStack } from '../primitives/Stack';
import { Text } from '../primitives/Text';

interface SocialMediaSelectProps {
  value: SocialMedia;
  onSelect: (value: SocialMedia) => void;
  error?: string;
  /**
   * When true the field is pre-selected and cannot be changed: the picker is
   * rendered read-only, visually muted, and announced as disabled to screen
   * readers (WCAG AA). Used by the LinkedIn enrichment entry point.
   */
  locked?: boolean;
}

const socialMediaOptions: {
  value: SocialMedia;
  labelKey: string;
  icon: string;
}[] = [
  { value: 'LinkedIn', labelKey: 'platforms.linkedin', icon: 'in' },
  { value: 'Facebook', labelKey: 'platforms.facebook', icon: 'f' },
  { value: 'Twitter', labelKey: 'platforms.twitter', icon: '𝕏' },
  { value: 'Instagram', labelKey: 'platforms.instagram', icon: 'ig' },
];

export const SocialMediaSelect = ({
  value,
  onSelect,
  error,
  locked = false,
}: SocialMediaSelectProps) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);

  const selectedOption = socialMediaOptions.find((opt) => opt.value === value);
  const selectedLabel = selectedOption
    ? t(`workflow.${selectedOption.labelKey}`)
    : t('workflow.selectPlatform');

  const fieldBody = (
    <Box
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      backgroundColor={locked ? colors.background.secondary : colors.background.primary}
      borderRadius="md"
      borderWidth={1}
      borderColor={error ? colors.status.error.main : colors.border.subtle}
      paddingX="md"
      paddingY="sm"
      minHeight={44}
      pointerEvents={locked ? 'none' : 'auto'}
      style={locked ? styles.lockedField : undefined}
    >
      <Text
        variant="body"
        color={(() => {
          if (locked) return colors.text.secondary;
          return selectedOption ? colors.text.primary : colors.text.secondary;
        })()}
      >
        {selectedLabel}
      </Text>
      <Text variant="body" color={colors.text.tertiary}>
        {locked ? '🔒' : '▼'}
      </Text>
    </Box>
  );

  return (
    <>
      <VStack spacing="xs">
        <Text variant="bodySmall" color={colors.text.primary} style={{ fontWeight: '500' }}>
          {t('workflow.socialMediaLabel')}
        </Text>
        {locked ? (
          <Box
            accessibilityLabel={`${t('workflow.socialMediaLabel')}: ${selectedLabel}. ${t('workflow.platformLockedHint')}`}
            accessibilityRole="text"
            accessibilityState={{ disabled: true }}
            aria-disabled={true}
            pointerEvents="none"
            style={styles.lockedField}
          >
            {fieldBody}
          </Box>
        ) : (
          <Pressable
            onPress={() => setModalVisible(true)}
            accessibilityLabel={t('workflow.socialMediaLabel')}
            accessibilityRole="button"
          >
            {fieldBody}
          </Pressable>
        )}
        {locked && (
          <Text variant="caption" color={colors.text.tertiary}>
            {t('workflow.platformLockedHint')}
          </Text>
        )}
        {error && (
          <Text variant="caption" color={colors.status.error.main}>
            {error}
          </Text>
        )}
      </VStack>

      {!locked && (
        <Sheet
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          position="center"
          size="sm"
          title={t('workflow.selectPlatform')}
        >
          <VStack spacing="sm">
            {socialMediaOptions.map((option) => (
              <TouchableOpacity
                key={option.value}
                onPress={() => {
                  onSelect(option.value);
                  setModalVisible(false);
                }}
                style={[styles.option, value === option.value && styles.selectedOption]}
                accessibilityLabel={t(`workflow.${option.labelKey}`)}
                accessibilityRole="button"
                accessibilityState={{ selected: value === option.value }}
              >
                <Box
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="space-between"
                  padding="md"
                  borderRadius="md"
                  backgroundColor={
                    value === option.value
                      ? withAlpha(colors.primary[500], 0.1)
                      : colors.background.primary
                  }
                >
                  <Text
                    variant="body"
                    color={value === option.value ? colors.primary[500] : colors.text.primary}
                  >
                    {t(`workflow.${option.labelKey}`)}
                  </Text>
                  {value === option.value && (
                    <Text variant="body" color={colors.primary[500]}>
                      ✓
                    </Text>
                  )}
                </Box>
              </TouchableOpacity>
            ))}
          </VStack>
          <TouchableOpacity
            onPress={() => setModalVisible(false)}
            style={[styles.cancelButton, { marginTop: 16 }]}
            accessibilityLabel={t('workflow.cancel')}
            accessibilityRole="button"
          >
            <Box
              backgroundColor={colors.background.secondary}
              borderRadius="md"
              padding="md"
              alignItems="center"
            >
              <Text variant="body" color={colors.text.primary}>
                {t('workflow.cancel')}
              </Text>
            </Box>
          </TouchableOpacity>
        </Sheet>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  lockedField: (Platform.OS === 'web' ? { cursor: 'default' as const } : {}) as ViewStyle,
  option: {
    width: '100%',
  },
  selectedOption: {
    borderWidth: 1,
    borderColor: colors.primary[500],
    borderRadius: 8,
  },
  cancelButton: {
    width: '100%',
  },
});
